import React from 'react';
import { useTranslation } from 'react-i18next';

const SIGNAL_COLORS = {
  'STRONG SELL': '#ef4444',
  'SELL': '#f97316',
  'NEUTRAL': '#6b7280',
  'BUY': '#eab308',
  'STRONG BUY': '#22c55e',
};

const SIGNAL_LABEL_KEYS = {
  'STRONG SELL': 'stats.strongSell',
  'SELL': 'stats.sell',
  'NEUTRAL': 'stats.neutral',
  'BUY': 'stats.buy',
  'STRONG BUY': 'stats.strongBuy',
};

const SUB_INDICATORS = [
  { key: 'momentum', labelKey: 'stats.momentum' },
  { key: 'whaleFlow', labelKey: 'stats.whaleFlow' },
  { key: 'funding', labelKey: 'stats.funding' },
  { key: 'liquidity', labelKey: 'stats.liquidity' },
  { key: 'oiPressure', labelKey: 'stats.oiPressure' },
];

function TraderMapSignalPanel({ signal }) {
  const { t } = useTranslation();
  const composite = signal?.composite ?? 50;
  const label = signal?.label || 'NEUTRAL';
  const description = signal?.description || t('stats.waitingForData');
  const color = SIGNAL_COLORS[label] || '#6b7280';

  // Gauge: 180-degree arc from -90 to +90 degrees
  // Needle rotation: -90 at score=0, +90 at score=100
  const needleAngle = -90 + (composite / 100) * 180;

  return (
    <div className="stats-panel stats-signal-panel">
      <div className="stats-panel-header">
        <span className="stats-panel-title">{t('stats.traderMapSignal')}</span>
      </div>

      <div className="stats-signal-gauge-wrap">
        <svg viewBox="0 0 108 62" className="stats-signal-gauge">
          {/* Background arc segments */}
          <path d="M 8 58 A 46 46 0 0 1 31 16" stroke="#ef4444" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.3" />
          <path d="M 31 16 A 46 46 0 0 1 54 12" stroke="#f97316" strokeWidth="5" fill="none" opacity="0.3" />
          <path d="M 44 12.5 A 46 46 0 0 1 64 12.5" stroke="#6b7280" strokeWidth="5" fill="none" opacity="0.3" />
          <path d="M 54 12 A 46 46 0 0 1 77 16" stroke="#eab308" strokeWidth="5" fill="none" opacity="0.3" />
          <path d="M 77 16 A 46 46 0 0 1 100 58" stroke="#22c55e" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.3" />

          {/* Needle */}
          <line
            x1="54"
            y1="58"
            x2="54"
            y2="18"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            transform={`rotate(${needleAngle}, 54, 58)`}
          />
          {/* Center dot */}
          <circle cx="54" cy="58" r="3" fill={color} />
        </svg>
      </div>

      <div className="stats-signal-score" style={{ color }}>
        {Math.round(composite)}
      </div>
      <div className="stats-signal-label" style={{ color }}>
        {t(SIGNAL_LABEL_KEYS[label] || 'stats.neutral')}
      </div>
      <div className="stats-signal-desc">{description}</div>

      <div className="stats-signal-bars">
        {SUB_INDICATORS.map(({ key, labelKey }) => {
          const lbl = t(labelKey);
          const val = signal?.[key] ?? 50;
          const barColor = val < 40 ? '#ef4444' : val > 60 ? '#22c55e' : '#6b7280';
          return (
            <div key={key} className="stats-signal-bar-row">
              <span className="stats-signal-bar-lbl">{lbl}</span>
              <div className="stats-signal-bar-track">
                <div
                  className="stats-signal-bar-fill"
                  style={{ width: `${Math.max(2, val)}%`, background: barColor }}
                />
              </div>
              <span className="stats-signal-bar-val">{Math.round(val)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { SIGNAL_COLORS, SUB_INDICATORS };
export default TraderMapSignalPanel;
